import { ReactNode } from 'react';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { useDashboardColors } from './panel';

export interface RankedRow {
    key: string;
    label: string;
    /// Linha menor embaixo do nome: canal, especialidade, valor.
    sublabel?: string;
    /// Etiqueta curta à esquerda, com a cor da série ("WA", "IG").
    chip?: ReactNode;
    value: number;
    color: string;
    tooltip?: string;
    /// Texto entre o valor e a porcentagem ("· 3 ganhos").
    extra?: string;
    /// Tons apagados para o que não é categoria de verdade ("Sem motivo").
    muted?: boolean;
    selected?: boolean;
}

interface Props {
    rows: RankedRow[];
    /// Base da porcentagem. Sem ela a fatia é da soma das linhas.
    total?: number;
    onSelect?: (row: RankedRow) => void;
    labelWidth?: number;
    /// Nome e número em cima, barra embaixo, em qualquer tela: em painel estreito a coluna do nome engoliria a barra.
    stacked?: boolean;
}

/// Barras ordenadas: nome, barra proporcional à maior e "valor · %"; clicar filtra o painel.
export const RankedBars = ({ rows, total, onSelect, labelWidth = 190, stacked = false }: Props) => {
    const cc = useDashboardColors();

    const base = total ?? rows.reduce((sum, row) => sum + row.value, 0);
    // Proporcional à maior linha, não ao total: com dez linhas parecidas, todas ficariam minúsculas.
    const largest = Math.max(1, ...rows.map((row) => row.value));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {rows.map((row) => {
                const share = base > 0 ? Math.round((row.value / base) * 100) : 0;

                const content = (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: stacked ? '1fr auto' : { xs: '1fr auto', sm: `${labelWidth}px 1fr auto` },
                            alignItems: 'center',
                            columnGap: 1.5,
                            rowGap: 0.5,
                            width: '100%',
                            py: 0.6,
                            px: 0.75,
                            borderRadius: '6px',
                            textAlign: 'left',
                            background: row.selected ? cc.accentSoftBg : 'transparent',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            {row.chip}
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        color: row.muted ? cc.textMuted : cc.textPrimary,
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        lineHeight: 1.25,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {row.label}
                                </Typography>
                                {row.sublabel && (
                                    <Typography sx={{ color: cc.textMuted, fontSize: '0.68rem', lineHeight: 1.2 }}>
                                        {row.sublabel}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                gridColumn: stacked ? '1 / -1' : { xs: '1 / -1', sm: 'auto' },
                                gridRow: stacked ? 2 : { xs: 2, sm: 'auto' },
                                height: 10,
                                borderRadius: '4px',
                                background: cc.countBg,
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    width: `${(row.value / largest) * 100}%`,
                                    minWidth: row.value > 0 ? 4 : 0,
                                    height: '100%',
                                    borderRadius: '4px',
                                    background: row.color,
                                    transition: 'width .25s',
                                }}
                            />
                        </Box>

                        <Typography
                            sx={{
                                color: cc.textPrimary,
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                fontVariantNumeric: 'tabular-nums',
                                textAlign: 'right',
                                minWidth: 64,
                            }}
                        >
                            {row.value.toLocaleString('pt-BR')}
                            <Box component="span" sx={{ color: cc.textMuted, fontWeight: 500, fontSize: '0.72rem' }}>
                                {row.extra ? ` · ${row.extra}` : ''} · {share}%
                            </Box>
                        </Typography>
                    </Box>
                );

                const line = onSelect ? (
                    <ButtonBase
                        key={row.key}
                        onClick={() => onSelect(row)}
                        aria-pressed={row.selected}
                        sx={{ display: 'block', width: '100%', borderRadius: '6px', '&:hover': { background: cc.countBg } }}
                    >
                        {content}
                    </ButtonBase>
                ) : (
                    <Box key={row.key}>{content}</Box>
                );

                return row.tooltip ? (
                    <Tooltip key={row.key} title={row.tooltip} placement="top" arrow>
                        {line}
                    </Tooltip>
                ) : (
                    line
                );
            })}
        </Box>
    );
};
